<template>
  <div>
    <div class="d-flex align-center mb-4">
      <v-btn icon :to="{ name: listRoute }"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <div>
        <div class="text-h6">{{ campaign.name || "Campanha" }}</div>
        <div class="text-caption text--secondary">
          {{ campaign.channel === "EMAIL" ? "E-mail" : "WhatsApp" }}
          · {{ campaignStatusLabel[campaign.status] || campaign.status }}
          <span v-if="campaign.template">
            · template {{ templateStatusLabel[campaign.template.status] }}
          </span>
        </div>
      </div>
    </div>

    <v-alert v-if="error" type="error" text dense class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="saved" type="success" text dense class="mb-4">Campanha salva.</v-alert>

    <template v-if="locked">
      <campaign-report
        :counts="counts"
        :channel="campaign.channel"
        :campaign-id="campaign.id"
        :api="api"
        :refresh-key="reportTick"
      />
    </template>

    <template v-else-if="campaign.id">
      <v-text-field v-model="name" label="Nome" outlined dense />

      <v-autocomplete
        v-model="partyPick"
        :items="partyResults"
        :search-input.sync="partyQuery"
        :loading="searching"
        item-text="name"
        item-value="id"
        return-object
        label="Adicionar evento"
        outlined
        dense
        hide-no-data
        no-filter
        clearable
        @change="addParty"
      >
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ formatDate(item.date) }}
              <span v-if="item.Organization"> · {{ item.Organization.name }}</span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>

      <div class="mb-4">
        <v-chip
          v-for="party in parties"
          :key="party.id"
          close
          class="mr-2 mb-2"
          @click:close="removeParty(party.id)"
        >
          {{ party.name }}
        </v-chip>
        <div v-if="!parties.length" class="text-caption text--secondary">
          Nenhum evento selecionado.
        </div>
      </div>

      <div class="mb-4">
        <v-switch
          v-model="filters.paidOnly"
          dense
          hide-details
          class="mt-0"
          label="Apenas ingressos válidos"
        />
        <div class="text-caption text--secondary mb-2">
          Somente pagamentos aprovados. Desligado, inclui qualquer status de pagamento.
        </div>
        <v-switch v-model="filters.dddEnabled" dense hide-details label="Filtrar por DDD" />
        <v-text-field
          v-if="filters.dddEnabled"
          v-model="filters.dddText"
          class="mt-2"
          label="DDDs"
          placeholder="11, 21, 43"
          hint="Separe por vírgula. Usa DDI 55 e telefone que começa com (DDD)."
          persistent-hint
          outlined
          dense
        />
        <v-switch
          v-model="filters.usedTicket"
          dense
          hide-details
          class="mt-3"
          label="Ingresso usado"
        />
        <div class="text-caption text--secondary mb-3">
          Quem tem ao menos um ingresso com entrada registrada.
        </div>
        <div v-if="parties.length" class="mt-2">
          <div class="text-body-2">
            <v-progress-circular
              v-if="audienceLoading"
              indeterminate
              size="16"
              width="2"
              class="mr-2"
            />
            <span v-else-if="audienceCount != null">
              {{ audienceCount.toLocaleString("pt-BR") }}
              {{ audienceCount === 1 ? "pessoa" : "pessoas" }} nesta seleção.
            </span>
          </div>
          <div v-if="audienceError" class="text-caption error--text">{{ audienceError }}</div>
          <div class="text-caption text--secondary">
            Número estimado. Os destinatários só são consultados ao iniciar o disparo.
          </div>
        </div>
      </div>

      <whatsapp-campaign-editor
        v-if="campaign.channel === 'WHATSAPP'"
        :key="'wa-' + (campaign.updatedAt || campaign.id)"
        v-model="editor"
        :upload-file="api.upload"
      />
      <email-campaign-editor
        v-else
        :key="'mail-' + (campaign.updatedAt || campaign.id)"
        v-model="editor"
        :upload="api.upload"
      />

      <div class="d-flex flex-wrap mt-6">
        <v-btn class="mr-2 mb-2" :loading="saving" @click="save">Salvar</v-btn>
        <v-btn
          v-if="campaign.channel === 'WHATSAPP'"
          class="mr-2 mb-2"
          color="primary"
          outlined
          :disabled="!canSubmit"
          :loading="submitting"
          @click="submit"
        >
          Enviar para análise
        </v-btn>
        <v-btn color="primary" class="mb-2" :disabled="!canStart" :loading="starting" @click="start">
          Iniciar
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import {
  campaignApi,
  campaignStatusLabel,
  templateStatusLabel,
} from "@/services/admin/campaigns";
import CampaignReport from "./CampaignReport.vue";
import WhatsappCampaignEditor from "./WhatsappCampaignEditor.vue";
import EmailCampaignEditor from "./EmailCampaignEditor.vue";

export default {
  components: { CampaignReport, WhatsappCampaignEditor, EmailCampaignEditor },
  props: {
    scope: { type: String, required: true },
  },
  data: () => ({
    campaignStatusLabel,
    templateStatusLabel,
    campaign: {},
    name: "",
    parties: [],
    filters: {
      paidOnly: false,
      dddEnabled: false,
      dddText: "",
      usedTicket: false,
    },
    editor: {},
    counts: {},
    reportTick: 0,
    partyQuery: "",
    partyPick: null,
    partyResults: [],
    searching: false,
    searchTimer: null,
    audienceCount: null,
    audienceLoading: false,
    audienceError: "",
    audienceTimer: null,
    audienceRequest: 0,
    error: "",
    saved: false,
    saving: false,
    submitting: false,
    starting: false,
    pollTimer: null,
  }),
  computed: {
    api() {
      return campaignApi(this.scope);
    },
    listRoute() {
      return this.scope === "ultimate" ? "ultimate.campaigns" : "admin.campaigns";
    },
    locked() {
      return this.campaign.status && this.campaign.status !== "DRAFT";
    },
    canSubmit() {
      const status = this.campaign.template?.status;
      return status === "DRAFT" || status === "DENIED";
    },
    canStart() {
      return this.campaign.template?.status === "APPROVED";
    },
  },
  watch: {
    partyQuery(value) {
      clearTimeout(this.searchTimer);
      if (!value || value.length < 2) {
        this.partyResults = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.searchParties(value), 300);
    },
    parties: {
      handler() {
        if (!this.campaign.id || this.locked) return;
        this.scheduleAudience();
      },
      deep: true,
    },
    filters: {
      handler() {
        if (!this.campaign.id || this.locked) return;
        this.scheduleAudience();
      },
      deep: true,
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    clearInterval(this.pollTimer);
    clearTimeout(this.searchTimer);
    clearTimeout(this.audienceTimer);
  },
  methods: {
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("pt-BR");
    },
    applyCampaign(campaign) {
      this.campaign = campaign;
      this.name = campaign.name || "";
      this.parties = campaign.parties || [];
      const filters = campaign.filters || {};
      this.filters = {
        paidOnly: !!filters.paidOnly,
        dddEnabled: !!filters.dddEnabled,
        dddText: Array.isArray(filters.ddds) ? filters.ddds.join(", ") : "",
        usedTicket: !!filters.usedTicket,
      };
      this.editor = { ...(campaign.template?.editor || {}) };
    },
    audienceFilters() {
      return {
        paidOnly: this.filters.paidOnly,
        dddEnabled: this.filters.dddEnabled,
        ddds: String(this.filters.dddText || "")
          .split(",")
          .map((value) => value.trim())
          .filter((value) => /^\d{2}$/.test(value)),
        usedTicket: this.filters.usedTicket,
      };
    },
    async load() {
      this.error = "";
      try {
        const campaign = await this.api.get(this.$route.params.id);
        this.applyCampaign(campaign);
        if (campaign.status !== "DRAFT") await this.loadReport();
      } catch (error) {
        this.error = error.message || "Não foi possível carregar a campanha";
      }
    },
    async loadReport() {
      const report = await this.api.report(this.$route.params.id);
      this.counts = report.counts || {};
      this.reportTick += 1;
      if (report.campaign) this.campaign = { ...this.campaign, ...report.campaign };
      if (this.campaign.status === "PROCESSING" && !this.pollTimer) {
        this.pollTimer = setInterval(() => this.loadReport().catch(() => {}), 5000);
      }
      if (this.campaign.status === "COMPLETED") {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async searchParties(query) {
      this.searching = true;
      try {
        const result = await this.api.searchParties({ query, take: 8 });
        this.partyResults = result.parties || [];
      } catch (error) {
        this.error = error.message || "Não foi possível buscar eventos";
      } finally {
        this.searching = false;
      }
    },
    addParty(party) {
      if (!party || !party.id) return;
      if (!this.parties.some((item) => item.id === party.id)) {
        this.parties.push(party);
      }
      this.partyPick = null;
      this.partyQuery = "";
    },
    removeParty(id) {
      this.parties = this.parties.filter((party) => party.id !== id);
    },
    scheduleAudience() {
      clearTimeout(this.audienceTimer);
      this.audienceError = "";
      if (!this.parties.length) {
        this.audienceCount = null;
        this.audienceLoading = false;
        return;
      }
      this.audienceLoading = true;
      this.audienceTimer = setTimeout(() => this.loadAudience(), 400);
    },
    async loadAudience() {
      const request = ++this.audienceRequest;
      const partyIds = this.parties.map((party) => party.id);
      try {
        const result = await this.api.audience(this.campaign.id, {
          partyIds,
          filters: this.audienceFilters(),
        });
        if (request !== this.audienceRequest) return;
        this.audienceCount = Number(result.count) || 0;
      } catch (error) {
        if (request !== this.audienceRequest) return;
        this.audienceCount = null;
        this.audienceError = error.message || "Não foi possível estimar o público";
      } finally {
        if (request === this.audienceRequest) this.audienceLoading = false;
      }
    },
    payload() {
      return {
        name: this.name,
        partyIds: this.parties.map((party) => party.id),
        filters: this.audienceFilters(),
        template: this.editor,
      };
    },
    async save() {
      this.saving = true;
      this.error = "";
      this.saved = false;
      try {
        const campaign = await this.api.update(this.campaign.id, this.payload());
        this.applyCampaign(campaign);
        this.saved = true;
      } catch (error) {
        this.error = error.message || "Não foi possível salvar";
      } finally {
        this.saving = false;
      }
    },
    async submit() {
      this.submitting = true;
      this.error = "";
      try {
        await this.api.update(this.campaign.id, this.payload());
        const campaign = await this.api.submit(this.campaign.id);
        this.applyCampaign(campaign);
      } catch (error) {
        this.error = error.message || "Não foi possível enviar para análise";
      } finally {
        this.submitting = false;
      }
    },
    async start() {
      this.starting = true;
      this.error = "";
      try {
        await this.api.update(this.campaign.id, this.payload());
        const report = await this.api.start(this.campaign.id);
        if (report.campaign) this.applyCampaign(report.campaign);
        this.counts = report.counts || {};
        await this.loadReport();
      } catch (error) {
        this.error = error.message || "Não foi possível iniciar a campanha";
      } finally {
        this.starting = false;
      }
    },
  },
};
</script>
